import type { CartTracking } from '@/features/cart/cart-v1/cart-v1-type';

import React from 'react';

import dynamic from 'next/dynamic';

import CartOverlayDesktop from '@/features/cart/cart-v1/overlay/cart-overlay-desktop';
import useCart from '@/features/cart/service/use-cart';
import { useCheckoutTracking } from '@/features/checkout/services/use-checkout-tracking';

const CartCtaDesktop = dynamic(() => import(/* webpackChunkName: "Cart" */ './cart-cta-desktop'));

type CartCtaWrapperProps = {
    activitySpotId: number;
    isDisabled: boolean;
    isOverlayInitiallyOpen: boolean;
    schoolId: number;
    tracking: CartTracking | null;
};

export default function CartCtaWrapperDesktop({
    activitySpotId,
    isDisabled,
    isOverlayInitiallyOpen,
    schoolId,
    tracking,
}: CartCtaWrapperProps) {
    const [isOverlayOpen, setOverlayOpen] = React.useState(false);
    const hasInitiallyOpened = React.useRef(false);

    const { bookingRedirectUrl, cartItems, changeItemCount, isLoading, schoolInfo, totalCount, totalPriceString } =
        useCart({
            activitySpotId,
            onCartEmpty: () => setOverlayOpen(false),
            schoolId,
        });

    const { trackCartOverlay } = useCheckoutTracking();

    React.useEffect(() => {
        if (isOverlayInitiallyOpen && !hasInitiallyOpened.current) {
            hasInitiallyOpened.current = true;
            setOverlayOpen(true);
        }
    }, [isOverlayInitiallyOpen, setOverlayOpen]);

    if (isDisabled || cartItems.length === 0 || !schoolInfo || !bookingRedirectUrl) {
        return null;
    }

    const handleOverlayTracking = () => {
        if (!tracking) {
            return;
        }
        trackCartOverlay(tracking);
    };

    const handleOverlayOpen = () => {
        handleOverlayTracking();
        setOverlayOpen(true);
    };

    return (
        <>
            <CartCtaDesktop
                onClick={handleOverlayOpen}
                schoolInfo={schoolInfo}
                totalCount={totalCount}
                totalPriceString={totalPriceString}
            />
            {isOverlayOpen && (
                <CartOverlayDesktop
                    bookingRedirectUrl={bookingRedirectUrl}
                    cartItems={cartItems}
                    changeItemCount={changeItemCount}
                    isLoading={isLoading}
                    onClose={() => setOverlayOpen(false)}
                    schoolInfo={schoolInfo}
                    totalPriceString={totalPriceString}
                />
            )}
        </>
    );
}
